/**
 * Session storage — the access_token returned by /identity/passkey/login/finish.
 *
 * Lives in localStorage for Phase 2 #4b. Two reasons we don't use cookies yet:
 * (1) the API host is on `*.execute-api.amazonaws.com` while the SPA is hosted
 * separately, so SameSite cookies don't ride along on the cross-origin fetch.
 * (2) Phase 2B's custom-domain work moves both onto `*.dev.re-platform.app`
 * and we can move to Secure + SameSite=Strict cookies then.
 *
 * Everything is best-effort: localStorage may be unavailable (private mode,
 * embedded webviews). loadSession() returns null and we redirect to /login.
 */

const TOKEN_KEY = "rp.session.token";
const META_KEY = "rp.session.meta";
const REFRESH_KEY = "rp.session.refresh";
const COGNITO_KEY = "rp.session.cognito";

/**
 * Refresh this many seconds BEFORE the access token actually expires.
 *
 * The access token lives 15 minutes (SEC-4). Requests are made with whatever
 * `getToken()` returns, so a token that expires mid-flight fails the request
 * that carried it — a margin wider than the slowest request is what stops that.
 * 90s covers an upload presign plus clock skew between the browser and Lambda.
 */
const REFRESH_MARGIN_SECONDS = 90;

export interface SessionMeta {
  userId: string;
  tenantId: string;
  primaryRole: string;
  /** Unix seconds — UI checks against this before attaching the bearer. */
  expiresAt: number;
  email: string;
  displayName: string | null;
}

export interface StoredSession extends SessionMeta {
  accessToken: string;
}

export function saveSession(input: {
  accessToken: string;
  refreshToken?: string;
  expiresIn: number;
  userId: string;
  tenantId: string;
  primaryRole: string;
  email: string;
  displayName: string | null;
}): void {
  const meta: SessionMeta = {
    userId: input.userId,
    tenantId: input.tenantId,
    primaryRole: input.primaryRole,
    expiresAt: Math.floor(Date.now() / 1000) + input.expiresIn,
    email: input.email,
    displayName: input.displayName,
  };
  try {
    localStorage.setItem(TOKEN_KEY, input.accessToken);
    localStorage.setItem(META_KEY, JSON.stringify(meta));
    if (input.refreshToken) {
      localStorage.setItem(REFRESH_KEY, input.refreshToken);
    }
  } catch {
    // localStorage unavailable — the user will be asked to sign in again next
    // load. No graceful fallback in dev; production gets cookies in Phase 2B.
  }
}

export function loadSession(): StoredSession | null {
  try {
    const token = localStorage.getItem(TOKEN_KEY);
    const raw = localStorage.getItem(META_KEY);
    if (!token || !raw) return null;
    const meta = JSON.parse(raw) as SessionMeta;
    if (
      typeof meta.expiresAt !== "number" ||
      meta.expiresAt < Date.now() / 1000
    ) {
      // Drop the spent ACCESS token but keep the refresh token: it outlives the
      // access token by days, so this session is renewable, not over. Calling
      // clearSession() here would throw away the credential that recovers it —
      // which is what made the 15-minute logout unrecoverable.
      try {
        localStorage.removeItem(TOKEN_KEY);
      } catch {
        // ignore
      }
      return null;
    }
    return { accessToken: token, ...meta };
  } catch {
    return null;
  }
}

/**
 * True when the session is over and the user must sign in again.
 *
 * An expired ACCESS token no longer qualifies on its own — while a refresh token
 * is still held the session is renewable, and redirecting to /login on the 15
 * minute mark is precisely the bug this replaced.
 */
export function sessionExpired(): boolean {
  try {
    const raw = localStorage.getItem(META_KEY);
    if (!raw) return false;
    if (readRefreshToken()) return false;
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) return false;
    const meta = JSON.parse(raw) as SessionMeta;
    return (
      typeof meta.expiresAt === "number" && meta.expiresAt < Date.now() / 1000
    );
  } catch {
    return false;
  }
}

export function clearSession(): void {
  try {
    localStorage.removeItem(TOKEN_KEY);
    // A half-filled onboarding wizard belongs to the person who was signed in.
    // Left behind, the NEXT account on this browser resumes it — restored to
    // their review step with somebody else's answers in it.
    localStorage.removeItem("rp.onboarding_draft");
    localStorage.removeItem(META_KEY);
    localStorage.removeItem(REFRESH_KEY);
    localStorage.removeItem(COGNITO_KEY);
  } catch {
    // ignore
  }
}

// -----------------------------------------------------------------------------
// Refresh — the access token lives 15 minutes; the refresh token lives 7 days.
//
// The backend has issued a refresh token since SEC-4 and this app simply threw
// it away, so every session died mid-task at the 15-minute mark (Checkpoint #17
// #12). Two properties of the server's design shape everything below:
//
//   ROTATION IS SINGLE-USE. /identity/token/refresh REVOKES the token you
//   present before minting the next pair. So two concurrent refreshes with the
//   same token means the second one gets "Refresh token has been revoked" —
//   a forced logout that looks exactly like the bug we are fixing, but
//   intermittent. Hence the single-flight promise below.
//
//   ANOTHER TAB SHARES THE STORAGE. A second tab may have rotated the token
//   since this module last read it, so the refresh token is re-read from
//   localStorage at the moment of use, never captured in a closure.
// -----------------------------------------------------------------------------

const API_BASE_URL =
  (import.meta.env.VITE_API_BASE_URL as string | undefined) ??
  window.location.origin;

/** In-flight refresh, shared by every caller until it settles. */
let inFlight: Promise<string | null> | null = null;

function readRefreshToken(): string | null {
  try {
    return localStorage.getItem(REFRESH_KEY);
  } catch {
    return null;
  }
}

async function doRefresh(): Promise<string | null> {
  // Read at the moment of use — a sibling tab may have rotated it since.
  const refreshToken = readRefreshToken();
  if (!refreshToken) return null;

  let res: Response;
  try {
    res = await fetch(`${API_BASE_URL}/identity/token/refresh`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ refresh_token: refreshToken }),
    });
  } catch {
    // Offline or the API is unreachable. NOT a credential problem — keep the
    // session so the user is still signed in when connectivity returns.
    return null;
  }

  if (!res.ok) {
    // 401 here means the token is genuinely spent, revoked or expired. Anything
    // else (5xx) is the server's problem, not the user's — don't log them out
    // over it, the access token may still have life left.
    if (res.status === 401) clearSession();
    return null;
  }

  const body = (await res.json()) as {
    access_token: string;
    expires_in: number;
    refresh_token: string;
  };

  try {
    const raw = localStorage.getItem(META_KEY);
    if (raw) {
      const meta = JSON.parse(raw) as SessionMeta;
      meta.expiresAt = Math.floor(Date.now() / 1000) + body.expires_in;
      localStorage.setItem(META_KEY, JSON.stringify(meta));
    }
    localStorage.setItem(TOKEN_KEY, body.access_token);
    localStorage.setItem(REFRESH_KEY, body.refresh_token);
  } catch {
    // Storage went away mid-session; the token we return is still usable for
    // this request, the next load will ask for a sign-in.
  }
  return body.access_token;
}

/**
 * Rotate the session, collapsing concurrent callers into ONE network call.
 *
 * Returns the new access token, or null when the session could not be renewed
 * (no refresh token, or the server rejected it — in which case the session has
 * been cleared and the caller should route to sign-in).
 */
export function refreshSession(): Promise<string | null> {
  inFlight ??= doRefresh().finally(() => {
    inFlight = null;
  });
  return inFlight;
}

/**
 * The access token to send, refreshing first if it is about to expire.
 *
 * This is the app's `getToken`, so it runs before every request — which is what
 * makes the margin above sufficient for long tasks: a 40-minute upload session
 * re-checks on each call rather than relying on the token it started with.
 */
export async function getAccessToken(): Promise<string | null> {
  const session = loadSession();
  if (session) {
    if (session.expiresAt - Date.now() / 1000 > REFRESH_MARGIN_SECONDS) {
      return session.accessToken;
    }
    return (await refreshSession()) ?? session.accessToken;
  }
  // loadSession() clears and returns null once expiresAt passes, but the refresh
  // token outlives it by days — so an expired access token is recoverable right
  // up until the refresh token itself dies.
  return refreshSession();
}

// -----------------------------------------------------------------------------
// Cognito creds — only populated when the user logs in via Auth0 Universal
// Login (Phase 2 #5B). Passkey-only logins skip this; AWS-SDK calls (S3
// presigned PUT) require a Universal Login round-trip until we wire passkey
// -> Cognito federation in a later phase.
// -----------------------------------------------------------------------------

export interface StoredCognitoCreds {
  accessKeyId: string;
  secretAccessKey: string;
  sessionToken: string;
  expiresAt: number;
  identityId: string;
}

export function saveCognitoCreds(creds: StoredCognitoCreds): void {
  try {
    localStorage.setItem(COGNITO_KEY, JSON.stringify(creds));
  } catch {
    // ignore — caller will fall back to "re-login required" UX
  }
}

export function loadCognitoCreds(): StoredCognitoCreds | null {
  try {
    const raw = localStorage.getItem(COGNITO_KEY);
    if (!raw) return null;
    const creds = JSON.parse(raw) as StoredCognitoCreds;
    if (creds.expiresAt < Date.now() / 1000) {
      localStorage.removeItem(COGNITO_KEY);
      return null;
    }
    return creds;
  } catch {
    return null;
  }
}
